import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import IsoDatePicker from '../../schedule/components/IsoDatePicker'
import MoneyInput from '../components/MoneyInput'
import { emptyInvoiceForm, formFromInvoice, invoiceFormProblem, invoiceInput, todayIso, type InvoiceForm } from '../lib/invoices'
import type { CostContract, CostInvoice } from '../types'

const NONE = 'none'

const Field: React.FC<{ id: string; label: string; required?: boolean; className?: string; children: React.ReactNode }> = ({ id, label, required, className, children }) => (
  <div className={cn('space-y-1.5', className)}>
    <Label htmlFor={id}>{label}{required ? ' *' : ''}</Label>
    {children}
  </div>
)

interface Props {
  open: boolean
  mode: 'create' | 'edit' | 'fromApplication'
  invoice?: CostInvoice | null
  /** The contracts an invoice may bill (invoiceContractOptions). Only the create-mode picker renders them; edit mode
   * never sends it (M4 — editing does not let the contract be changed). */
  contracts?: CostContract[]
  initialContractId?: string | null
  /** Retention % to start from: the contract's on create (from `contracts`), the application's contract's from an application. */
  initialRetentionPercent?: string | null
  currency: string
  onOpenChange: (open: boolean) => void
  /** Resolves true when the server accepted the input; the dialog stays open on failure. */
  onSubmit: (input: Record<string, unknown>) => Promise<boolean>
}

const InvoiceDialog: React.FC<Props> = ({ open, mode, invoice, contracts = [], initialContractId, initialRetentionPercent, currency, onOpenChange, onSubmit }) => {
  const { t } = useTranslation()
  const [form, setForm] = useState<InvoiceForm>(() => emptyInvoiceForm(null, todayIso()))
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const formMode = mode === 'create' ? 'create' : 'edit'
  const retentionOf = (contractId: string | null) => contracts.find((c) => c.id === contractId)?.retentionPercent ?? null

  useEffect(() => {
    if (!open) return
    setForm(invoice ? formFromInvoice(invoice)
      : emptyInvoiceForm(initialContractId ?? null, todayIso(), mode === 'create' ? retentionOf(initialContractId ?? null) : initialRetentionPercent ?? null))
    setTouched(false)
    setSubmitting(false)
    // Only on opening: a background reload must not wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const set = <K extends keyof InvoiceForm>(key: K, value: InvoiceForm[K]) => setForm((f) => ({ ...f, [key]: value }))
  const problem = invoiceFormProblem(form, formMode)
  const submit = async () => {
    setTouched(true)
    if (problem || submitting) return
    setSubmitting(true)
    const ok = await onSubmit(invoiceInput(form, formMode))
    setSubmitting(false)
    if (ok) onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t(mode === 'edit' ? 'CostControl_invoice_edit' : 'CostControl_invoice_new')}</DialogTitle>
          {mode !== 'edit' && <DialogDescription>{t(mode === 'create' ? 'CostControl_invoice_dialog_hint' : 'CostControl_invoice_from_application_hint')}</DialogDescription>}
        </DialogHeader>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); submit() }}>
          {mode === 'create' && (
            <Field id="cc-inv-contract" label={t('CostControl_invoice_field_contract')} required className="sm:col-span-2">
              <Select value={form.contractId ?? NONE} onValueChange={(v) => setForm((f) => ({ ...f, contractId: v === NONE ? null : v, retentionPercent: retentionOf(v === NONE ? null : v), retentionValid: true }))}>
                <SelectTrigger id="cc-inv-contract"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {!form.contractId && <SelectItem value={NONE}>{t('CostControl_change_pick_contract')}</SelectItem>}
                  {contracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.number ? `${c.number} · ${c.name}` : c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {(contracts.find((c) => c.id === form.contractId)?.applicationCount ?? 0) > 0 && (
                <p className="mt-2 text-sm text-destructive">{t('CostControl_invoice_direct_on_applications')}</p>
              )}
            </Field>
          )}
          <Field id="cc-inv-number" label={t('CostControl_invoice_field_number')} required>
            <Input id="cc-inv-number" value={form.number} onChange={(e) => set('number', e.target.value)} autoFocus={mode === 'edit'} />
          </Field>
          {mode === 'create' && (
            <Field id="cc-inv-amount" label={`${t('CostControl_invoice_field_amount')} (${currency})`} required>
              <MoneyInput id="cc-inv-amount" value={form.amount} onChange={(value, valid) => setForm((f) => ({ ...f, amount: value, amountValid: valid }))} />
            </Field>
          )}
          <Field id="cc-inv-issue" label={t('CostControl_invoice_field_issueDate')} required>
            <IsoDatePicker id="cc-inv-issue" value={form.issueDate} onChange={(v) => set('issueDate', v)} />
          </Field>
          <Field id="cc-inv-due" label={t('CostControl_invoice_field_dueDate')}>
            <IsoDatePicker id="cc-inv-due" value={form.dueDate} onChange={(v) => set('dueDate', v)} clearable invalid={touched && problem === 'CostControl_error_dates_order'} />
          </Field>
          <Field id="cc-inv-retention" label={t('CostControl_invoice_field_retentionPercent')}>
            <MoneyInput id="cc-inv-retention" value={form.retentionPercent} onChange={(value, valid) => setForm((f) => ({ ...f, retentionPercent: value, retentionValid: valid }))} />
          </Field>
          <Field id="cc-inv-note" label={t('CostControl_invoice_field_note')} className="sm:col-span-2">
            <Textarea id="cc-inv-note" rows={3} value={form.note} onChange={(e) => set('note', e.target.value)} />
          </Field>
          {touched && problem && <p className="text-sm text-destructive sm:col-span-2">{t(problem)}</p>}
          <div className="flex items-center justify-end gap-2 border-t pt-4 sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{t('Common_cancel')}</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t(mode === 'edit' ? 'Common_save' : 'Common_create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default InvoiceDialog
